import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import Div from "@jumbo/shared/Div";
import { useDebouncedCallback } from "beautiful-react-hooks";
import { IconButton, InputAdornment } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useSearch } from "./SearchContext";

const JumboSearch = ({ onChange, value, sx, placeholder, page }) => {
  const { search, query } = useSearch();
  const isControlled = value !== undefined;
  const [searchKeywords, setSearchKeywords] = useState(
    (query && query[page]) || ""
  );

  const inputRef = React.useRef(null);

  const handleImmediateChange = (event) => {
    const next = event?.target?.value || "";
    if (page) {
      search(page, next);
    }
    onChange(next);
  };

  const handleDebouncedChange = useDebouncedCallback(
    (event) => {
      setSearchKeywords(event?.target.value || "");
    },
    [],
    500
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (!isControlled && inputRef.current) {
      // Restore from query context or localStorage
      const fromContext = (query && query[page]) || "";
      const fromStorage = (() => {
        try {
          return localStorage.getItem(`search.${page}`) || "";
        } catch (e) {
          return "";
        }
      })();
      const initial = fromContext || fromStorage || "";
      inputRef.current.value = initial;
      setSearchKeywords(initial);
      if (initial && page) {
        search(page, initial);
      }
    }
  }, []);

  const handleClear = () => {
    if (isControlled) {
      if (page) {
        search(page, "");
      }
      onChange("");
    } else {
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      setSearchKeywords("");
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (!isControlled) {
      if (page) {
        search(page, searchKeywords);
      }
      // Persist to localStorage
      try {
        localStorage.setItem(`search.${page}`, searchKeywords || "");
      } catch (e) {}
      onChange(searchKeywords);
    }
  }, [searchKeywords]);

  React.useEffect(() => {
    return () => handleDebouncedChange.cancel();
  });

  return (
    <Div
      sx={{
        position: "relative",
        width: "100%",
        ...sx,
      }}
    >
      <Div
        sx={{
          padding: (theme) => theme.spacing(0, 2),
          height: "100%",
          position: "absolute",
          pointerEvents: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2,
        }}
      >
        <SearchIcon />
      </Div>
      <InputBase
        sx={{
          color: "inherit",
          display: "flex",
          borderRadius: 24,

          "& .MuiInputBase-input": {
            padding: (theme) => theme.spacing(1, 1, 1, 0),
            // vertical padding + font size from searchIcon
            paddingLeft: (theme) => `calc(1em + ${theme.spacing(4)})`,
            transition: (theme) => theme.transitions.create("width"),
            width: "100%",
            height: 24,
          },
        }}
        inputRef={inputRef}
        placeholder={placeholder}
        inputProps={{ "aria-label": "search" }}
        onChange={isControlled ? handleImmediateChange : handleDebouncedChange}
        value={isControlled ? value : undefined}
        endAdornment={
          <InputAdornment position="end" sx={{ mr: 2 }}>
            <IconButton onClick={handleClear} edge="end">
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        }
      />
    </Div>
  );
};

export default React.memo(JumboSearch);
